import { useCallback, useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookmarkPlus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Saved wage/fuel rate presets — quick seasonal switching for the cost inputs.
export default function RatePresets({ cost, setCost }) {
  const [presets, setPresets] = useState([]);
  const [appliedId, setAppliedId] = useState(""); // last applied/saved preset (for delete + label)
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/rate-presets").then(({ data }) => setPresets(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setAppliedId(id);
    setCost({ ...cost, ...p.cost });
    toast.success(`Applied "${p.name}" rates`);
  };

  const applied = presets.find((p) => p.id === appliedId);

  const savePreset = async () => {
    if (!name.trim()) {
      toast.error("Enter a preset name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/rate-presets", { name: name.trim(), cost });
      setPresets((prev) => {
        const rest = prev.filter((p) => p.id !== data.id);
        return [...rest, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setAppliedId(data.id);
      setName("");
      setNaming(false);
      toast.success(`Preset "${data.name}" saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save preset");
    } finally {
      setSaving(false);
    }
  };

  const deleteApplied = async () => {
    if (!appliedId) return;
    try {
      await api.delete(`/rate-presets/${appliedId}`);
      setPresets((prev) => prev.filter((p) => p.id !== appliedId));
      setAppliedId("");
      toast.success("Preset deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="rounded-md bg-stone-50 border border-border p-3">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-[#57534E]">Rate Presets</Label>
      <div className="mt-1.5 flex items-center gap-2">
        {/* value stays "" so onValueChange fires even when re-applying the same preset */}
        <Select value="" onValueChange={applyPreset}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid="rate-preset-select">
            <SelectValue
              placeholder={
                applied
                  ? `Applied: ${applied.name}`
                  : presets.length
                  ? "Apply a saved preset…"
                  : "No presets saved yet"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {presets.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`rate-preset-option-${p.id}`}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs border-[#F97316]/40 text-[#EA580C] hover:bg-[#FFF7ED]"
          onClick={() => setNaming((v) => !v)}
          data-testid="rate-preset-save-toggle"
        >
          <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Save current
        </Button>
        {applied && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={deleteApplied} data-testid="rate-preset-delete">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {naming && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            className="h-8 text-xs"
            placeholder="Preset name, e.g. Summer 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && savePreset()}
            data-testid="rate-preset-name-input"
          />
          <Button
            size="sm"
            className="h-8 text-xs bg-[#F97316] hover:bg-[#EA580C] text-white"
            onClick={savePreset}
            disabled={saving}
            data-testid="rate-preset-save-confirm"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}
